/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { Badge } from '@/components/ui/Badge/badge';
import { deparmentCVA } from '@/components/ui/Badge/deparmentCVA';
import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { worksTableConfig } from '@/config/table-configs';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { knownDepartments } from '@certhon/domain-models';
import { Work } from '@certhon/models';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';

const h = createColumnHelper<Work>();

// TODO: make schema driven so we get correct filters for free
const columns: ColumnDef<Work, any>[] = [
  h.accessor('recnum', { header: 'Recnum' }),
  h.accessor('id', { header: 'ID' }),
  h.accessor('oms', { header: 'Descr.' }),
  h.accessor('oms2', { header: 'Descr. 2' }),
  {
    id: 'department',
    header: 'Department',
    enableSorting: true,
    enableMultiSort: true,
    accessorFn: (work: Work) => work.afd || ' ',
    cell: ({ row }) => {
      const char = row.original.afd;
      if (!char) return '-';
      const dep = knownDepartments.find((d) => d.char === char);
      return (
        <Badge
          key={row.id}
          variant="outline"
          className={deparmentCVA({ variant: (dep?.char || '_') as '_' })}
        >
          {dep?.name || char}
        </Badge>
      );
    },
  },
  h.accessor('klant', { header: 'Client ID' }),
  h.accessor('klant_naam', { header: 'Client' }),
];

export { DataTable } from '@/components/ui/data-table/data-table';

const emptyArray: any[] = [];

// TODO: allow disabling selection for DataTables
export function WorksTable({
  uriPrefix = '',
  data,
  isLoading,
}: {
  uriPrefix?: string;
} & DataTableConsumer<Work>) {
  const { filterValue, setFilterValue } = usePanelFilterState({ prefix: 'w.' });
  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState({ prefix: 'w.' });

  // Clear selection when filter changes (context change)
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  // Use schema-driven filter system with minimal fields
  const {
    tokenDefinitions,
    filteredData,
    columns: filterableColumns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<Work>({
    fields: {}, // No schema fields defined yet for works
    data: data || emptyArray,
    filterValue,
    customColumns: columns,
    excludeColumns: [],
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(worksTableConfig, filterableColumns),
    [filterableColumns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    'works',
    completeDefaultConfig,
  );

  return (
    <DataTable<Work, unknown>
      data={filteredData}
      isLoading={isLoading}
      columns={filterableColumns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={(w) => [uriPrefix, '/works/', w.recnum].join('')}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="works"
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
    />
  );
}
